"use client";

import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect, useRef, useSyncExternalStore } from "react";
import {
  buildThreadDetails,
  isHiddenPath,
  PLAIN_APP_ID,
  type PlainCustomer,
  type PlainCustomerDetails,
  type PlainThreadDetails,
  toCustomerDetails,
} from "./plainConfig";
import { useUserSession } from "./UserSessionProvider";

interface PlainInitConfig {
  appId: string;
  hideLauncher?: boolean;
  /**
   * Visitors we have not identified must verify their email (Plain sends a
   * one-time code) before they can chat. Without this an unidentified visitor
   * becomes "Anonymous Customer #N", whom nobody can reply to. This applies
   * to logged-out prospects on marketing/login pages; signed-in clients
   * already carry a verified emailHash and are not challenged.
   */
  requireAuthentication?: boolean;
  customerDetails?: PlainCustomerDetails;
  threadDetails?: PlainThreadDetails;
}

interface PlainWidget {
  /**
   * Documented as returning nothing, but the script resolves a promise once
   * its handshake with Plain has finished; `update` is only honoured after
   * that point.
   */
  init(config: PlainInitConfig): Promise<void> | void;
  /** Replaces top-level fields; nested objects are not merged. */
  update(config: {
    hideLauncher?: boolean;
    threadDetails?: PlainThreadDetails;
  }): void;
  setCustomerDetails(details: PlainCustomerDetails): void;
}

declare global {
  interface Window {
    Plain?: PlainWidget;
  }
}

/**
 * Whether `Plain.init` has finished. The script only applies `update` once
 * its handshake is done, and its own `isInitialized()` is false for that
 * whole window, so anything pushed before then has to be replayed after.
 */
let widgetReady = false;
const readyListeners = new Set<() => void>();

function markWidgetReady() {
  widgetReady = true;
  for (const listener of readyListeners) listener();
}

function subscribeReady(listener: () => void) {
  readyListeners.add(listener);
  return () => readyListeners.delete(listener);
}

function getWidgetReady() {
  return widgetReady;
}

function getServerWidgetReady() {
  return false;
}

/**
 * What the widget has confirmed receiving. Written only after a call
 * succeeded: a failed `init` or push must leave it stale so the next replay
 * sends the identity again instead of assuming the widget already has it.
 * Keeps the replay after `ready` from resending what `init` carried, and a
 * re-render with an unchanged identity silent.
 */
const lastPushed = { launcher: "", customer: "" };

/**
 * How long `init` may take before the widget is treated as ready anyway. A
 * handshake that never settles would otherwise leave every later identity
 * push waiting forever; the script queues setCustomerDetails behind init
 * itself, so pushing early is harmless.
 */
const INIT_READY_TIMEOUT_MS = 15_000;

/**
 * The widget's surface is verified against a deployed script, not a contract.
 * A method that starts throwing must not take the page down on every route
 * change; the chat degrades, the app does not. Returns whether the call went
 * through so the caller can decide what counts as delivered.
 */
function pushToWidget(action: () => void): boolean {
  try {
    action();
    return true;
  } catch (error) {
    console.warn("Plain widget call failed", error);
    return false;
  }
}

export function PlainComponent() {
  const session = useUserSession();
  const pathname = usePathname();
  const ready = useSyncExternalStore(
    subscribeReady,
    getWidgetReady,
    getServerWidgetReady,
  );
  const hideLauncher = isHiddenPath(pathname);

  const sessionName = session?.name;
  const customer: PlainCustomer | undefined = session
    ? {
        email: session.email,
        emailHash: session.plainHash,
        fullName:
          sessionName && sessionName.length > 0 ? sessionName : undefined,
      }
    : undefined;
  const customerDetails = toCustomerDetails(customer);
  const threadDetails = buildThreadDetails(customer?.program);
  // Effects key on the serialised form: the objects above are rebuilt every
  // render, and re-pushing an unchanged identity would spam the widget.
  const customerKey = JSON.stringify(customerDetails);
  const threadKey = JSON.stringify(threadDetails);

  // `onLoad` closes over the render that created it, so it reads the config
  // through a ref: a slow script then initialises with whatever identity has
  // resolved by the time it arrives, instead of the first render's.
  const latestConfig = useRef({ hideLauncher, customerDetails, threadDetails });
  useEffect(() => {
    latestConfig.current = { hideLauncher, customerDetails, threadDetails };
  });

  useEffect(() => {
    if (!ready) return;
    const plain = window.Plain;
    if (!plain) return;
    const launcherKey = JSON.stringify({ hideLauncher, threadKey });
    if (launcherKey === lastPushed.launcher) return;
    const delivered = pushToWidget(() =>
      plain.update({
        hideLauncher,
        threadDetails: JSON.parse(threadKey) as PlainThreadDetails,
      }),
    );
    if (delivered) lastPushed.launcher = launcherKey;
  }, [ready, hideLauncher, threadKey]);

  // Pushed in both directions: logout is a same-tab navigation, so this
  // component outlives the session and the widget would otherwise keep
  // attributing chats to the person who just signed out.
  useEffect(() => {
    if (!ready) return;
    const plain = window.Plain;
    if (!plain) return;
    if (customerKey === lastPushed.customer) return;
    const delivered = pushToWidget(() =>
      plain.setCustomerDetails(JSON.parse(customerKey) as PlainCustomerDetails),
    );
    if (delivered) lastPushed.customer = customerKey;
  }, [ready, customerKey]);

  return (
    <Script
      src="https://chat.cdn-plain.com/index.js"
      onLoad={() => {
        const plain = window.Plain;
        if (!plain) {
          // Nothing to talk to, but the page must not sit in "loading" forever.
          console.warn("Plain script loaded without window.Plain");
          markWidgetReady();
          return;
        }
        const config = latestConfig.current;
        // Recorded as delivered only once init resolves; a failed init leaves
        // lastPushed stale so the replay after `ready` sends the identity.
        const carried = {
          launcher: JSON.stringify({
            hideLauncher: config.hideLauncher,
            threadKey: JSON.stringify(config.threadDetails),
          }),
          customer: JSON.stringify(config.customerDetails),
        };
        // Once the fallback has fired, the effects own lastPushed: they may
        // have delivered a newer identity than `carried`, and a late init
        // resolving afterwards must not roll the bookkeeping back to it.
        let fallbackFired = false;
        const readyFallback = setTimeout(() => {
          fallbackFired = true;
          markWidgetReady();
        }, INIT_READY_TIMEOUT_MS);
        try {
          Promise.resolve(
            plain.init({
              appId: PLAIN_APP_ID,
              requireAuthentication: true,
              hideLauncher: config.hideLauncher,
              customerDetails: config.customerDetails.email
                ? config.customerDetails
                : undefined,
              threadDetails: config.threadDetails,
            }),
          )
            .then(() => {
              if (fallbackFired) return;
              lastPushed.launcher = carried.launcher;
              lastPushed.customer = carried.customer;
            })
            .catch(() => undefined)
            .finally(() => {
              clearTimeout(readyFallback);
              markWidgetReady();
            });
        } catch {
          // A synchronous throw must not leave the widget "never ready": every
          // later identity push would be dropped, the very failure this fixes.
          clearTimeout(readyFallback);
          markWidgetReady();
        }
      }}
    />
  );
}
